// ./src/config/fuelTypes.ts
// Fuel type configuration for vehicle fuel/energy sources
// Used by car forms, selectors, and helper functions

// ============================================
// Types
// ============================================

export interface FuelTypeOption {
  /** Value stored in database */
  value: string;
  /** Translation key - use with t('common:fuelTypes.gasoline') */
  labelKey: string;
  /** Icon name for UI components */
  iconName: string;
}

// ============================================
// Fuel Type Options
// ============================================

export const FUEL_TYPE_OPTIONS: FuelTypeOption[] = [
  // Common fuels
  { value: "gasoline", labelKey: "fuelTypes.gasoline", iconName: "GasStation" },
  { value: "diesel", labelKey: "fuelTypes.diesel", iconName: "GasStation" },
  { value: "electric", labelKey: "fuelTypes.electric", iconName: "BatteryCharging" },
  // Gas fuels
  { value: "lpg", labelKey: "fuelTypes.lpg", iconName: "Flame" },
  { value: "cng", labelKey: "fuelTypes.cng", iconName: "Flame" },
  // Alternative fuels
  { value: "e85", labelKey: "fuelTypes.e85", iconName: "Leaf" },
  { value: "biodiesel", labelKey: "fuelTypes.biodiesel", iconName: "Leaf" },
  { value: "hydrogen", labelKey: "fuelTypes.hydrogen", iconName: "Atom" },
];

// ============================================
// Lookup Map
// ============================================

export const FUEL_TYPE_MAP: Record<string, FuelTypeOption> = Object.fromEntries(
  FUEL_TYPE_OPTIONS.map((opt) => [opt.value, opt])
);

// ============================================
// Category Sets (used by carHelpers for logic)
// ============================================

/** Fuel types that use electricity (kWh) - for efficiency calculations */
export const ELECTRIC_FUEL_TYPES: ReadonlySet<string> = new Set([
  "electric",
  "hydrogen",
]);

/** Fuel types that use liquid volume (liters/gallons) */
export const LIQUID_FUEL_TYPES: ReadonlySet<string> = new Set([
  "gasoline",
  "diesel",
  "e85",
  "biodiesel",
]);

/** Fuel types that use gas volume (LPG, CNG) */
export const GAS_FUEL_TYPES: ReadonlySet<string> = new Set([
  "lpg",
  "cng",
]);

// ============================================
// Helper Functions
// ============================================

/**
 * Get fuel type option by value
 * @param value - Fuel type value
 * @returns Fuel type option or undefined
 */
export const getFuelTypeOption = (
  value: string | null | undefined
): FuelTypeOption | undefined => {
  if (!value) return undefined;
  return FUEL_TYPE_MAP[value];
};

/**
 * Get icon name for a fuel type
 * @param value - Fuel type value
 * @returns Icon name or default
 */
export const getFuelTypeIcon = (value: string | null | undefined): string => {
  return getFuelTypeOption(value)?.iconName ?? "GasStation";
};